import L from 'leaflet';
import { EventBus } from '../core/EventBus.js';
import { escapeHtml } from '../utils/formatting.js';
import { CATEGORY_COLORS, DEFAULT_COLOR } from './ShipMarker.js';

const DEFAULT_OWNER_COLOR = '#888888';

function buildShipSvg(fill, ownerStroke) {
  return `<svg viewBox="0 0 24 24" width="22" height="22" xmlns="http://www.w3.org/2000/svg">` +
    `<polygon points="12,2 19,20 12,16 5,20" fill="${fill}" fill-opacity="0.85" stroke="${ownerStroke}" stroke-width="1.6" stroke-linejoin="round"/>` +
    `</svg>`;
}

/**
 * Renders every OTHER player's ships on the shared map — the vesselfinder/
 * marinetraffic-style world view (src/core/GameState.js's otherShips,
 * populated from the server's periodic world:ships broadcast). Deliberately
 * a separate class + separate Leaflet layer ('otherShips') from ShipMarker
 * ('ships', the current player's own fleet) rather than folding into it:
 *   - own ships get exact real-time positions (unicast deltas + client-side
 *     interpolation, see main.js's _tickShipInterpolation); other players'
 *     ships only get a position snapshot every few seconds, so their marker
 *     just jumps to the latest known spot — different enough behavior to
 *     keep as a distinct code path.
 *   - the "hide other players' ships" / per-category filters (ShipFilterControl.js)
 *     need a layer they can toggle independently of the player's own fleet,
 *     which must always stay visible.
 *
 * sync() does a full replace each call (matching the payload's own
 * full-replace semantics) rather than incremental upsert/remove pairs —
 * simpler and correct since GameState.getAllOtherShips() is already the
 * complete current list, not a delta.
 */
export class OtherShipMarker {
  constructor(mapLayers) {
    this.mapLayers = mapLayers;
    this.markers   = new Map(); // shipId -> L.marker
  }

  /**
   * @param {Array<Object>} ships  GameState.getAllOtherShips() — plain data
   *   objects: { id, name, type, category, position, heading, status,
   *   ownerId, companyName, companyColor }.
   * @param {(ship: Object) => boolean} [isVisible]  Optional per-ship filter
   *   (category checkboxes / "show other players" toggle) — a ship failing
   *   this is removed from the map without being dropped from `ships`.
   */
  sync(ships, isVisible = () => true) {
    const seen = new Set();
    for (const ship of ships) {
      if (!isVisible(ship)) continue;
      seen.add(ship.id);
      this._upsert(ship);
    }
    for (const id of [...this.markers.keys()]) {
      if (!seen.has(id)) this._remove(id);
    }
  }

  _upsert(ship) {
    const latlng = [ship.position.lat, ship.position.lng];
    const fill = CATEGORY_COLORS[ship.category] || DEFAULT_COLOR;
    const ownerStroke = ship.companyColor || DEFAULT_OWNER_COLOR;
    const heading = ship.heading || 0;
    // Leaflet's tooltip content is set via innerHTML (DivOverlay._updateContent)
    // when given a string — ship.name and ship.companyName are both
    // player-controlled (rename, company setup) and rendered here in every
    // OTHER player's browser, so this MUST be escaped or it's a stored XSS.
    const label = `${escapeHtml(ship.name)} — ${escapeHtml(ship.companyName)}`;

    if (!this.markers.has(ship.id)) {
      const icon = L.divIcon({
        html:       `<div class="ship-arrow other-ship-arrow" style="transform:rotate(${heading}deg)">${buildShipSvg(fill, ownerStroke)}</div>`,
        className:  'ship-marker-icon other-ship-marker-icon',
        iconSize:   [22, 22],
        iconAnchor: [11, 11]
      });

      const marker = L.marker(latlng, { icon, zIndexOffset: 50, interactive: true })
        .addTo(this.mapLayers.getLayer('otherShips'));

      marker.bindTooltip(label, { direction: 'top', className: 'ship-label other-ship-label' });

      marker.on('click', () => {
        EventBus.emit('ui:otherShipSelected', { ship });
      });

      this.markers.set(ship.id, marker);
    } else {
      const marker = this.markers.get(ship.id);
      marker.setLatLng(latlng);

      const el = marker.getElement();
      if (el) {
        const arrow = el.querySelector('.ship-arrow');
        if (arrow) {
          arrow.style.transform = `rotate(${heading}deg)`;
          const poly = arrow.querySelector('polygon');
          if (poly) {
            poly.setAttribute('fill', fill);
            poly.setAttribute('stroke', ownerStroke);
          }
        }
      }

      marker.setTooltipContent(label);
    }
  }

  _remove(shipId) {
    const marker = this.markers.get(shipId);
    if (marker) {
      marker.remove();
      this.markers.delete(shipId);
    }
  }
}
